import { QATAR_DIAL_CODE } from '@fe/shared/Countries';
import type { ComponentChildren } from 'preact';
import { createContext } from 'preact';
import { useContext, useMemo } from 'preact/hooks';

import { extractPhoneParts } from '@/helpers/functions/phone';
import {
  E_FloatingWidgetTriggerLocation,
  E_FloatingWidgetMessageLocation,
  E_FloatingWidgetMobileMessageLocation,
} from '@/types/FloatingWidgetTrigger';
import type { I_WidgetConfig, T_WidgetConfigContext } from '@/types/Widget';

const defaultConfig: T_WidgetConfigContext = {
  widgetLocation: E_FloatingWidgetTriggerLocation.BOTTOM_LEFT,
  mobileMessageLocation: E_FloatingWidgetMobileMessageLocation.ABOVE,
  messageLocation: E_FloatingWidgetMessageLocation.RIGHT,
  countryCode: QATAR_DIAL_CODE,
  phoneNumber: '',
  email: undefined,
  firstName: undefined,
  lastName: undefined,
  isRegistered: false,
  storefrontLoginURL: undefined,
  appName: 'Enable',
  fullRedemptionOnly: false,
  showMessages: true,
};

export const WidgetConfigContext = createContext<T_WidgetConfigContext>(defaultConfig);

export const useWidgetConfig = () => useContext(WidgetConfigContext);

export function WidgetConfigProvider({
  initialConfig,
  children,
}: {
  initialConfig: I_WidgetConfig;
  children: ComponentChildren;
}) {
  const { customer = {} } = initialConfig;
  const { phone: customerPhoneNumber = '', email, firstName, lastName } = customer;
  const isRegistered = Boolean(email);

  const { countryCode, phoneNumber } = useMemo(
    () => extractPhoneParts(customerPhoneNumber),
    [customerPhoneNumber],
  );

  const contextValue = useMemo<T_WidgetConfigContext>(
    () => ({
      widgetLocation: initialConfig.widgetLocation ?? E_FloatingWidgetTriggerLocation.BOTTOM_LEFT,
      mobileMessageLocation:
        initialConfig.mobileMessageLocation ?? E_FloatingWidgetMobileMessageLocation.ABOVE,
      messageLocation: initialConfig.messageLocation ?? E_FloatingWidgetMessageLocation.RIGHT,
      customerPhoneNumber,
      countryCode,
      phoneNumber,
      email,
      firstName,
      lastName,
      isRegistered,
      storefrontLoginURL: initialConfig.storefrontLoginURL ?? undefined,
      theme: initialConfig.theme,
      appName: initialConfig.appName || 'Enable',
      showNationality: initialConfig.showNationality ?? false,
      fullRedemptionOnly: initialConfig?.fullRedemptionOnly ?? false,
      showMessages: initialConfig.showMessages ?? true,
    }),
    [initialConfig, countryCode, phoneNumber, isRegistered],
  );

  return (
    <WidgetConfigContext.Provider value={contextValue}>{children}</WidgetConfigContext.Provider>
  );
}
